import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';

import { QuestionVerificationStatus } from '../../../../interfaces/IWorkflow';
import colors from '../../../../theme/colors';
import { InputSize, LabelSize, secondaryInputMargin, sizes, spacings } from '../../../../theme/variables';
import { APP_TOOLTIP_ID } from '../../../common/Tooltip/constants';
import Container from '../../Container';
import FlexBox from '../../FlexBox';
import { InfoIcon } from '../../icons';
import Label, { LabelProps } from '../../Label';
import QuestionVerificationStatusIcon from '../../QuestionVerificationStatusIcon';

export interface LabeledInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: LabelProps['children'];
  required: boolean | undefined;
  inline: boolean | undefined;
  secondary: boolean | undefined;
  disabled: boolean | undefined;
  inputSize?: InputSize;
  labelSize?: LabelSize;
  customCss?: SerializedStyles;
  containerCSS?: SerializedStyles;
  labelContainerCss?: SerializedStyles;
  info?: string;
  labelDescription?: React.JSX.Element | string | null;
  verificationStatus?: QuestionVerificationStatus;
}

const labelWidth = (labelSize: LabelSize, secondary: boolean) => {
  return secondary ? sizes[labelSize] - secondaryInputMargin : sizes[labelSize];
};

const LabeledInput = ({
  id,
  label,
  required,
  inline = false,
  secondary = false,
  disabled = false,
  children,
  inputSize = InputSize.Medium,
  labelSize = LabelSize.Medium,
  customCss,
  containerCSS,
  labelContainerCss,
  info,
  verificationStatus,
  labelDescription,
  ...rest
}: LabeledInputProps) => {
  const reservedSpaceForInfo = info ? spacings.px24 : spacings.px0;

  return inline ? (
    <FlexBox
      alignItemsBaseline
      customCss={css`
        flex-grow: 1;
        ${labelContainerCss};
      `}
    >
      <Label
        id={`${id}-label`}
        htmlFor={id}
        required={!!required}
        pv={spacings.px8}
        ph={spacings.px4}
        ml={secondary ? secondaryInputMargin : spacings.px0}
        customCss={css`
          flex: 0 0 ${labelWidth(labelSize, secondary) - reservedSpaceForInfo}px;
          color: ${secondary || disabled ? colors.neutral600 : colors.black};
          white-space: normal;
          overflow-wrap: break-word;
          ${customCss}
        `}
      >
        {label}
        {labelDescription && (
          <div
            css={css`
              color: ${colors.neutral600};
              font-weight: 500;
              font-size: 14px;
            `}
          >
            {labelDescription}
          </div>
        )}
      </Label>
      {verificationStatus && (
        <Container>
          <QuestionVerificationStatusIcon verificationStatus={verificationStatus} disabled={disabled} />
        </Container>
      )}
      {!!info && (
        <Container
          ml={spacings.px4}
          customCss={css`
            position: relative;
            bottom: 2px;
          `}
        >
          <InfoIcon
            color={colors.black}
            data-testid={`info-${id}`}
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={info}
          />
        </Container>
      )}
      <Container
        css={css`
          margin-left: ${labelSize === LabelSize.Small ? spacings.px4 : spacings.px12}px;
          flex: 0 1 ${sizes[inputSize]}px;
        `}
      >
        {children}
      </Container>
    </FlexBox>
  ) : (
    <Container {...rest} customCss={containerCSS}>
      <Label id={`${id}-label`} htmlFor={id} required={!!required} mb={4} customCss={customCss}>
        {label}
      </Label>
      {children}
    </Container>
  );
};

export default LabeledInput;
